import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export default function Admin({ session }) {
  const [members, setMembers] = useState([])
  const [selectedMember, setSelectedMember] = useState(null)
  const [memberScores, setMemberScores] = useState([])
  const [memberCheckins, setMemberCheckins] = useState([])
  const [memberCoaching, setMemberCoaching] = useState([])
  const [memberSeverity, setMemberSeverity] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('overview')
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    checkAdminAndLoad()
  }, [])

  async function checkAdminAndLoad() {
    setLoading(true)
    const { data: adminData } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', session.user.id)
      .single()

    if (!adminData) {
      setIsAdmin(false)
      setLoading(false)
      return
    }

    setIsAdmin(true)
    await fetchMembers()
    setLoading(false)
  }

  async function fetchMembers() {
    const { data: usersData } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })

    if (!usersData) return

    const membersWithData = await Promise.all(usersData.map(async user => {
      const [{ data: score }, { data: checkin }, { data: profile }] = await Promise.all([
        supabase.from('afib_scores').select('*').eq('user_id', user.id).order('score_date', { ascending: false }).limit(1).single(),
        supabase.from('daily_checkins').select('checkin_date').eq('user_id', user.id).order('checkin_date', { ascending: false }).limit(1).single(),
        supabase.from('profiles').select('*').eq('user_id', user.id).single()
      ])

      const daysSinceCheckin = checkin?.checkin_date
        ? Math.floor((new Date() - new Date(checkin.checkin_date + 'T00:00:00')) / (1000 * 60 * 60 * 24))
        : null

      const chadsvasc = calcChadsvasc(profile)

      return {
        ...user,
        latestScore: score?.scaled_score ?? null,
        lastCheckin: checkin?.checkin_date ?? null,
        daysSinceCheckin,
        profile,
        chadsvasc,
        weeklyCompleted: score?.weekly_completed ?? false
      }
    }))

    setMembers(membersWithData)
  }

  function calcChadsvasc(profile) {
    if (!profile) return null
    let score = 0
    if (profile.chf) score += 1
    if (profile.htn) score += 1
    if (profile.age >= 75) score += 2
    else if (profile.age >= 65) score += 1
    if (profile.diabetes) score += 1
    if (profile.prior_stroke_tia) score += 2
    if (profile.vascular_disease) score += 1
    if (profile.sex === 'female') score += 1
    return score
  }

  async function fetchMemberDetail(userId) {
    const [{ data: scores }, { data: checkins }, { data: coaching }, { data: severity }] =
      await Promise.all([
        supabase.from('afib_scores').select('*').eq('user_id', userId).order('score_date', { ascending: false }).limit(14),
        supabase.from('daily_checkins').select('*').eq('user_id', userId).order('checkin_date', { ascending: false }).limit(14),
        supabase.from('coaching_log').select('*').eq('user_id', userId).order('log_date', { ascending: false }).limit(10),
        supabase.from('afib_severity').select('*').eq('user_id', userId).order('recorded_at', { ascending: false })
      ])

    setMemberScores(scores || [])
    setMemberCheckins(checkins || [])
    setMemberCoaching(coaching || [])
    setMemberSeverity(severity || [])
  }

  function formatDate(dateStr) {
    if (!dateStr) return '--'
    const d = new Date(dateStr + 'T00:00:00')
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
  }

  function scoreColor(score) {
    if (score === null) return '#888'
    if (score >= 75) return '#085041'
    if (score >= 50) return '#BA7517'
    return '#A32D2D'
  }

  function scoreBg(score) {
    if (score === null) return '#F1EFE8'
    if (score >= 75) return '#E1F5EE'
    if (score >= 50) return '#FAEEDA'
    return '#FCEBEB'
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', fontFamily: 'Arial' }}>
      <p style={{ color: '#185FA5' }}>Loading admin panel...</p>
    </div>
  )

  if (!isAdmin) return (
    <div style={{ background: 'white', borderRadius: 12, padding: 24, textAlign: 'center', fontFamily: 'Arial' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C', marginBottom: 8 }}>Access restricted</div>
      <div style={{ fontSize: 13, color: '#666' }}>You don't have admin privileges. Contact the system administrator.</div>
    </div>
  )

  // ── MEMBER DETAIL VIEW ───────────────────────────
  if (view === 'detail' && selectedMember) {
    const member = selectedMember
    return (
      <div style={{ fontFamily: 'Arial' }}>
        <button onClick={() => { setView('overview'); setSelectedMember(null) }} style={{
          background: 'none', border: 'none', color: '#185FA5', fontSize: 13,
          fontWeight: 600, cursor: 'pointer', marginBottom: 12, padding: 0
        }}>← Back to overview</button>

        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C' }}>{member.username || member.email}</div>
              <div style={{ fontSize: 12, color: '#888' }}>Member since {formatDate(member.created_at?.split('T')[0])}</div>
            </div>
            <div style={{
              background: scoreBg(member.latestScore),
              borderRadius: 10, padding: '8px 14px', textAlign: 'center'
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(member.latestScore) }}>
                {member.latestScore ?? '--'}
              </div>
              <div style={{ fontSize: 10, color: '#888' }}>AFib score</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              ['CHA₂DS₂-VASc', member.chadsvasc ?? '--', member.chadsvasc >= 2 ? '#FCEBEB' : '#E1F5EE', member.chadsvasc >= 2 ? '#A32D2D' : '#085041'],
              ['Last check-in', member.lastCheckin ? formatDate(member.lastCheckin) : '--', member.daysSinceCheckin > 3 ? '#FCEBEB' : '#E1F5EE', member.daysSinceCheckin > 3 ? '#A32D2D' : '#085041'],
              ['Age / Sex', member.profile ? `${member.profile.age} / ${member.profile.sex}` : '--', '#F1EFE8', '#444'],
            ].map(([label, val, bg, color]) => (
              <div key={label} style={{ background: bg, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>
        </div>

        {member.profile && (
          <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Medical history</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[
                ['Hypertension', member.profile.htn],
                ['Diabetes', member.profile.diabetes],
                ['Hyperlipidemia', member.profile.hyperlipidemia],
                ['Sleep apnea', member.profile.has_sleep_apnea],
                ['CHF', member.profile.chf],
                ['Vascular disease', member.profile.vascular_disease],
                ['Prior stroke/TIA', member.profile.prior_stroke_tia],
                ['Nicotine use', member.profile.nicotine_use],
                ['Alcoholism', member.profile.alcoholism],
              ].filter(([, val]) => val).map(([label]) => (
                <span key={label} style={{ background: '#FAEEDA', color: '#633806', fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 600 }}>
                  {label}
                </span>
              ))}
              {member.profile.anticoag_prescribed && (
                <span style={{ background: '#E6F1FB', color: '#0C447C', fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 600 }}>
                  On {member.profile.anticoag_medication || 'anticoagulation'}
                </span>
              )}
            </div>
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Recent scores (last 14 days)</div>
          {memberScores.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>No scores recorded yet</div>
          ) : (
            memberScores.map(s => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <span style={{ fontSize: 12, color: '#888' }}>{formatDate(s.score_date)}</span>
                <div style={{ flex: 1, margin: '0 12px', height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                  <div style={{ width: `${s.scaled_score}%`, height: 6, background: scoreColor(s.scaled_score), borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(s.scaled_score), minWidth: 36, textAlign: 'right' }}>{s.scaled_score}</span>
                {!s.weekly_completed && <span style={{ fontSize: 10, color: '#185FA5', background: '#E6F1FB', padding: '1px 6px', borderRadius: 6, marginLeft: 6 }}>daily</span>}
              </div>
            ))
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Recent check-ins</div>
          {memberCheckins.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>No check-ins yet</div>
          ) : (
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {['Date', 'Exercise', 'Sleep', 'Alcohol', 'Anticoag'].map(h => (
                    <th key={h} style={{ padding: '4px 6px', textAlign: 'left', color: '#888', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {memberCheckins.map(c => (
                  <tr key={c.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 6px', color: '#555' }}>{formatDate(c.checkin_date)}</td>
                    <td style={{ padding: '6px 6px', color: '#333' }}>{c.exercise_minutes}min</td>
                    <td style={{ padding: '6px 6px', color: parseFloat(c.sleep_hours) < 6 ? '#A32D2D' : '#333' }}>{c.sleep_hours}h</td>
                    <td style={{ padding: '6px 6px', color: c.alcohol_level === 'none' ? '#085041' : '#A32D2D' }}>{c.alcohol_level}</td>
                    <td style={{ padding: '6px 6px', color: c.anticoag_taken ? '#085041' : c.anticoag_applicable ? '#A32D2D' : '#888' }}>
                      {c.anticoag_applicable ? (c.anticoag_taken ? '✅' : '❌') : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {memberSeverity.length > 0 && (
          <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>AFib severity assessments</div>
            {memberSeverity.map(s => {
              const total = s.frequency_score + s.duration_score + s.severity_score + s.symptom_score + s.wellbeing_score
              return (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: 11, background: '#E6F1FB', color: '#0C447C', padding: '2px 8px', borderRadius: 8 }}>{s.assessment_type}</span>
                  <span style={{ fontSize: 12, color: '#888' }}>{formatDate(s.recorded_at)}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: total <= 15 ? '#085041' : total <= 25 ? '#BA7517' : '#A32D2D' }}>{total}/50</span>
                </div>
              )
            })}
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Recent coaching messages</div>
          {memberCoaching.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>No coaching messages yet</div>
          ) : (
            memberCoaching.map(msg => (
              <div key={msg.id} style={{ padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: '#888' }}>{msg.category}</span>
                  <span style={{ fontSize: 11, color: '#888' }}>{formatDate(msg.log_date)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>{msg.personalized_message}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 6,
                    background: msg.priority === 'high' ? '#FCEBEB' : msg.priority === 'medium' ? '#FAEEDA' : '#E1F5EE',
                    color: msg.priority === 'high' ? '#A32D2D' : msg.priority === 'medium' ? '#633806' : '#085041'
                  }}>{msg.priority}</span>
                  {msg.ai_personalized && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: '#EEEDFE', color: '#3C3489' }}>AI personalized</span>}
                  {msg.was_read && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: '#F1EFE8', color: '#5F5E5A' }}>Read</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    )
  }

  // ── OVERVIEW ─────────────────────────────────────
  const alerts = members.filter(m => m.daysSinceCheckin > 3 || (m.chadsvasc >= 2 && !m.profile?.anticoag_prescribed))

  return (
    <div style={{ fontFamily: 'Arial' }}>
      {alerts.length > 0 && (
        <div style={{ background: '#FCEBEB', borderRadius: 12, padding: '12px 16px', marginBottom: 12, border: '1px solid #F09595' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 8 }}>⚠️ Attention needed ({alerts.length})</div>
          {alerts.map(m => (
            <div key={m.id} style={{ fontSize: 12, color: '#A32D2D', marginBottom: 4 }}>
              {m.daysSinceCheckin > 3
                ? `${m.username || m.email} — no check-in for ${m.daysSinceCheckin} days`
                : `${m.username || m.email} — CHA₂DS₂-VASc ${m.chadsvasc} but no anticoagulation on record`}
            </div>
          ))}
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
          Member overview — {members.length} enrolled
        </div>

        {members.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '20px 0' }}>
            No members enrolled yet
          </div>
        ) : (
          members.map(member => (
            <div key={member.id}
              onClick={async () => {
                setSelectedMember(member)
                await fetchMemberDetail(member.id)
                setView('detail')
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer'
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', background: '#E6F1FB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#0C447C', flexShrink: 0
              }}>
                {(member.username || member.email || '?')[0].toUpperCase()}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2C2C2C' }}>
                  {member.username || member.email}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {member.lastCheckin ? `Last check-in: ${formatDate(member.lastCheckin)}` : 'No check-ins yet'}
                  {member.daysSinceCheckin > 3 && <span style={{ color: '#A32D2D', fontWeight: 600 }}> — {member.daysSinceCheckin} days ago</span>}
                </div>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{
                  background: scoreBg(member.latestScore),
                  borderRadius: 8, padding: '4px 10px', marginBottom: 4
                }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: scoreColor(member.latestScore) }}>
                    {member.latestScore ?? '--'}
                  </span>
                </div>
                {member.chadsvasc !== null && (
                  <div style={{ fontSize: 10, color: member.chadsvasc >= 2 ? '#A32D2D' : '#888' }}>
                    CHADS: {member.chadsvasc}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}